import {useContext, useState} from 'react';
import {Linking, ScrollView, StyleSheet, View} from 'react-native';
import {
  Button,
  Caption,
  IconButton,
  List,
  Surface,
  Text,
  Title,
  TouchableRipple,
  useTheme,
} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import firestore from '@react-native-firebase/firestore';

import {UserContext} from '../App';
import {Property, useProperties} from '../hooks/useDomainData';
import {useAuthPersonInfo} from '../hooks/usePersonInfo';
import {useMutation, useQueryClient} from 'react-query';
import {useAppSettings} from '../components/AppSettings';
import {PropertyEditor} from '../components/FormikStoreEditors/PropertyEditor';

function Properties() {
  const theme = useTheme();
  const user = useContext(UserContext);
  const person = useAuthPersonInfo();
  const properties = useProperties();
  const queryClient = useQueryClient();
  const appSettings = useAppSettings();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentFirestoreObject, setCurrentFirestoreObject] = useState<
    Property | undefined
  >();

  const collectionName = 'properties';
  const handleDeleteFirestoreObject = useMutation(
    (firestoreObject: Property) => {
      console.log('should delete: ' + firestoreObject.ref.path);
      return firestore()
        .collection(collectionName)
        .doc(firestoreObject.ref.id)
        .delete();
    },
    {
      onSettled: () => {
        console.log('deleting succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
    },
  );

  const handleEditFirestoreObject = (firestoreObject?: Property) => {
    console.log('open for edit or new object: ' + firestoreObject?.ref.path);
    setCurrentFirestoreObject(firestoreObject);
    setEditDialogVisible(true);
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <View style={styles.horizontalCentered}>
        <Title style={styles.title}>Properties</Title>
        {(person?.isAdmin || person?.isGuestRelations) && (
          <Button mode="outlined" onPress={() => handleEditFirestoreObject()}>
            Create Property
          </Button>
        )}
      </View>
      <ScrollView persistentScrollbar={true} style={[styles.content]}>
        {!properties && <Caption>loading...</Caption>}
        {properties?.map(home => {
          if (home.active || person?.isAdmin || person?.isGuestRelations) {
            return (
              <Surface key={home.propertyId} style={styles.focusedSurface}>
                <List.Accordion
                  title={home.propertyName}
                  titleStyle={!home.active ? styles.strikethrough : {}}>
                  <View style={styles.indentOnce}>
                    <Caption>
                      {home.propertyUrl && (
                        <TouchableRipple
                          onPress={() => Linking.openURL(home.propertyUrl)}>
                          <View>
                            <Caption>{home.fund?.fund}</Caption>
                            {!home.active && (
                              <Caption>
                                <Text>Inactive</Text>
                              </Caption>
                            )}
                            <View style={styles.row}>
                              <Text style={{color: theme.colors.primary}}>
                                See Calendar and Details in Partner Portal
                              </Text>
                              <Icon
                                style={styles.horizontalPad}
                                color={theme.colors.primary}
                                name="open-in-new"
                                size={20}
                              />
                            </View>
                          </View>
                        </TouchableRipple>
                      )}
                      <>
                        {(person?.isAdmin || person?.isGuestRelations) && (
                          <IconButton
                            icon="pencil"
                            size={18}
                            color={appSettings.currentTheme.colors.primary}
                            onPress={() => handleEditFirestoreObject(home)}
                          />
                        )}
                        {person?.isAdmin && (
                          <IconButton
                            icon="delete"
                            size={18}
                            color={appSettings.currentTheme.colors.primary}
                            onPress={() =>
                              handleDeleteFirestoreObject.mutate(home)
                            }
                          />
                        )}
                      </>
                    </Caption>
                  </View>
                </List.Accordion>
              </Surface>
            );
          }
        })}
      </ScrollView>
      <PropertyEditor
        dialogVisible={editDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setEditDialogVisible(visible);
          if (!visible) {
            setCurrentFirestoreObject(undefined);
          }
        }}
        property={currentFirestoreObject}
      />
    </>
  );
}

const styles = StyleSheet.create({
  indentOnce: {paddingLeft: 20},
  horizontalPad: {paddingHorizontal: 10},
  strikethrough: {textDecorationLine: 'line-through'},
  row: {flexDirection: 'row'},
  container: {
    flex: 1,
    position: 'relative',
  },
  content: {
    paddingHorizontal: 20,
  },
  center: {
    width: '100%',
    alignItems: 'center',
  },
  focusedSurface: {
    padding: 10,
    margin: 10,
    elevation: 5,
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    paddingHorizontal: 10,
  },
});

export default Properties;
